import React from 'react'
import { useLocation, matchPath } from 'react-router'

import { getLenders, Lender } from 'api/lenders'
import useClientApplication from 'hooks/useClientApplication'
import useUser from 'hooks/useUser'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import LenderName from 'state/enums/LenderName'
import useSwr from 'swr'

interface LenderContextValue extends Partial<Lender> {
    name: LenderName
    setLenderByName: React.Dispatch<React.SetStateAction<LenderName>>
}

export const LenderContext = React.createContext<LenderContextValue>({
    name: LenderName.ROADRUNNER_FINANCIAL,
    setLenderByName: () => undefined,
})

interface LendersContextValue {
    error?: Error
    isLoading: boolean
    lenders: Lender[]
}

const LendersContext = React.createContext<LendersContextValue>({
    lenders: [],
    isLoading: false,
})

const routes = [
    '/application/:applicationId',
    '/application/decision/:applicationId',
    '/application/status/:applicationId',
    '/contract-generation/:applicationId',
    '/copy-into-new/:applicationId',
    '/copy-vehicle/:applicationId',
]

const findApplicationId = (pathname: string) =>
    routes.reduce(
        (_applicationId, route) =>
            matchPath({ path: route }, pathname)?.params?.applicationId ??
            _applicationId,
        '',
    ) || undefined

const LenderContextProvider = (props: {
    readonly children: React.ReactNode
}) => {
    const [lenderName, setLenderName] = React.useState<LenderName>(
        LenderName.ROADRUNNER_FINANCIAL,
    )

    const { pathname } = useLocation()
    const { isLoggedIn } = useUser()

    const { data, error, isLoading } = useSwr(
        isLoggedIn ? 'v1/lenders' : null,
        getLenders,
        {
            errorRetryCount: 3,
        },
    )

    const { clientApplication } = useClientApplication(
        isLoggedIn ? findApplicationId(pathname) : undefined,
    )

    const values = React.useMemo(() => {
        const applicationLenderName =
            clientApplication?.lender_name as LenderName

        const _lenderName = applicationLenderName ?? lenderName
        const lenders = data?.objects ?? []

        const lender =
            lenders.find((_lender: Lender) => _lender.name === _lenderName) ??
            ({ name: _lenderName } as Lender)

        return {
            lenders: { error, isLoading, lenders },
            lender: { ...lender, setLenderByName: setLenderName },
        }
    }, [
        clientApplication,
        data?.objects,
        error,
        isLoading,
        lenderName,
        setLenderName,
    ])

    React.useEffect(() => {
        setLenderName(LenderName.ROADRUNNER_FINANCIAL)
    }, [pathname])

    const fetchSucceeded =
        isLoggedIn && !isLoading && !error && data !== undefined

    const lendersCount = data?.objects?.length ?? 0

    React.useEffect(() => {
        if (!fetchSucceeded || values.lender.id !== undefined) return

        if (lendersCount === 0) {
            OctaneLogger.warn(
                'LenderContextProvider: no lenders returned from v1/lenders',
                { lenderName: values.lender.name },
            )
        } else {
            OctaneLogger.warn(
                'LenderContextProvider: resolved lender has no id after successful lenders fetch',
                { lenderName: values.lender.name, lendersCount },
            )
        }
    }, [fetchSucceeded, lendersCount, values.lender.id, values.lender.name])

    return (
        <LendersContext.Provider value={values.lenders}>
            <LenderContext.Provider value={values.lender}>
                {props.children}
            </LenderContext.Provider>
        </LendersContext.Provider>
    )
}

export default LenderContextProvider

export const useLender = () => React.useContext(LenderContext)
export const useLenders = () => React.useContext(LendersContext)
export const useIsLenderHNB = () =>
    useLender().name === LenderName.HUNTINGTON_NATIONAL_BANK
